import React, { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MainContent } from '@bbkAdminComponents/app-layout/main-content';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import {
  LogInPaths,
  REMOVED_SELF_SERVE_PATHS,
} from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { TrialSignupPage3 } from '@bbkAdminComponents/trial-signup/TrialSignupPage3';
import {
  selectApp,
  selectAppState,
  selectCompany,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import { invitationSlice } from '@bbkAdminRedux/rtkq/invitation.slice';
import { AuthorizedRouter } from '@bbkAdminRouter/authorized.router';
import { LogoutPage } from '@bbkAdminRouter/logout.page';
import { SignupPaths } from '@bbkAdminRouter/signup-paths';
import { hardRedirect } from '@bbkAdminUtils/browser-navigation';
import { toSafeInternalRedirect } from '@bbkAdminUtils/safe-redirect';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
} from '@bbkShared/react-router';

const CompanySelectorPage = lazy(
  () => import('@bbkAdminComponents/CompanySelectorPage')
);
const InternalRouter = lazy(
  () => import('@bbkAdminComponents/internal/internal-router')
);

export const LoggedInRouter: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const app = useSelector(selectApp);
  const { data: invites = [], isFetching: isFetchingInvites } =
    invitationSlice.endpoints.getPendingInvitationsByEmail.useQuery();
  const loadedNecessaryData = useSelector(selectAppState).loadedNecessaryData;
  const { redirectUri } = useSearchParams<{ redirectUri: string }>();

  const onAuthLandingPath =
    location.pathname === LogInPaths.signIn ||
    location.pathname === LogInPaths.company ||
    location.pathname === `${LogInPaths.company}/`;

  // A validated same-origin `redirectUri` carried onto an auth-landing path:
  // the CBIDP IdP round-trip lands an already-authenticated user back on
  // /company/sign-in, and a mid-session re-login carries the page the user was
  // on as `redirectUri`. Resolved here so the effect below can hard-navigate.
  const deepLinkRedirect =
    onAuthLandingPath && redirectUri
      ? toSafeInternalRedirect(redirectUri, '')
      : '';

  useEffect(() => {
    const pathname = location.pathname;
    const adminAppPagesRegex =
      /^\/(trial_signup|company|internal|logout|cbidp-exchange)/;
    if (!adminAppPagesRegex.test(pathname)) {
      history.push(LogInPaths.company);
    }
  }, [history, location.pathname]);

  useEffect(() => {
    // Hard-navigate to the deep link (not a soft `history.push`) so `appInit`
    // re-resolves the active company/app from the *target* URL, which carries
    // the authoritative company AND app. This must NOT be gated on the current
    // company/app: on a mid-session re-login localStorage / `selected_company`
    // already resolve *some* company/app on the sign-in wrapper page, which
    // previously suppressed this redirect and let AuthorizedRouter soft-redirect
    // to the deep link without re-running appInit — leaving the active app
    // stale/mismatched and rendering an empty page.
    if (deepLinkRedirect) {
      hardRedirect(deepLinkRedirect);
    }
  }, [deepLinkRedirect]);

  useEffect(() => {
    // No deep link, but a multi-company user with no active company/app
    // resolved yet: send them to the selector.
    if (
      onAuthLandingPath &&
      !deepLinkRedirect &&
      !(company && app) &&
      user &&
      user.companies.length > 1
    ) {
      history.push(LogInPaths.selector);
    }
  }, [app, company, deepLinkRedirect, history, onAuthLandingPath, user]);

  useEffect(() => {
    // If a logged-in user has no companies associated with them, default to the Selector page
    if (user && user.companies.length === 0) {
      history.push(LogInPaths.selector);
    }
  }, [history, user]);

  // Still trying to set all items, wait until finished to choose what to show
  // FIXME: isFtechingInvites is critical here. It blocks following Routers to load.
  // Need to figure out how to not rely on isFetchingInvites
  //
  // `deepLinkRedirect` short-circuits to the loader while the hard navigation is
  // in flight, so AuthorizedRouter never gets to soft-redirect /company/sign-in
  // to the deep link (which would skip the appInit re-resolution we need).
  if (!loadedNecessaryData || isFetchingInvites || deepLinkRedirect) {
    return <SuspenseLoader message=" " />;
  }

  return (
    <Suspense
      fallback={
        <MainContent>
          <SuspenseLoader message=" " />
        </MainContent>
      }
    >
      <Switch>
        <Route path={SignupPaths.trialSignup3}>
          <TrialSignupPage3 />
        </Route>

        <Route path={LogInPaths.logout}>
          <LogoutPage />
        </Route>

        {/*
          Cognito self-serve auth pages were removed in GRW-2782. A logged-in
          user hitting one of these legacy URLs would otherwise fall through to
          AuthorizedRouter and be misread as a company key, so route them to
          LogoutPage as the base mfa-reset route did.
        */}
        <Route path={REMOVED_SELF_SERVE_PATHS}>
          <LogoutPage />
        </Route>

        <Route path={LogInPaths.selector} exact>
          <MainContent>
            <CompanySelectorPage />
          </MainContent>
        </Route>

        <Route path="/internal/">
          <MainContent>
            <InternalRouter />
          </MainContent>
        </Route>

        <Route>
          <AuthorizedRouter />
        </Route>
      </Switch>
    </Suspense>
  );
};
